/**
 * PropositionsSigneesWidget — Dashboard "Propositions ESS signées à valider"
 * (V36.14 — 29/05/2026).
 *
 * Accessible Super Admin / Admin / Cooper / Expert. Le cooper ne voit que
 * ses propres membres / propositions qu'il a envoyées.
 *
 * Actions :
 *  - Voir le PDF signé (téléchargement)
 *  - "Créer la souscription" (redirige vers /cooper/souscription-annuelle?proposition=…)
 *  - "Refuser" (saisie raison obligatoire)
 *  - Filtre statut : pending_validation / validated / refused / all
 */
import React from 'react';
import {
  Loader2, FileSignature, CheckCircle2, XCircle, Download,
  RefreshCw, Clock, ShieldCheck, Send, BellRing, Eye, FileText, Award, User, Calendar, Euro, History,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { fmtEur, fmtDate, STATUS_FILTERS } from './propositionsSigneesHelpers';
import { usePropositionsSignees } from './usePropositionsSignees';
import PropositionsSigneesModals from './PropositionsSigneesModals';

export default function PropositionsSigneesWidget() {
  const {
    items, counts, filter, setFilter, loading, relancingId,
    refuserOpen, setRefuserOpen, refuserTarget, refuserRaison, setRefuserRaison, refuserSubmitting,
    validerOpen, setValiderOpen, validerTarget, validerNote, setValiderNote, validerSubmitting,
    pdfLoadingId, pdfPreview, resendOpen, setResendOpen, resendSending, resendForm, setResendForm,
    emailLog, setEmailLog, emailLogLoadingId,
    fetchAll, openRefuser, submitRefuser, openValider, submitValider, handleRelancer,
    handleViewPdf, handleDownloadPdf, closePdfPreview, handleResendPdfByEmail, handleShowEmailLog,
  } = usePropositionsSignees();


  return (
    <div className="space-y-4" data-testid="propositions-signees-widget">
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg">
                <FileSignature className="h-5 w-5 text-purple-600" />
                Propositions ESS signées
              </CardTitle>
              <CardDescription className="mt-1 text-xs">
                Pilote la validation finale des propositions signées électroniquement
                par les destinataires (eIDAS SES).
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchAll()}
              disabled={loading}
              className="self-start"
              data-testid="propositions-signees-refresh"
            >
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Rafraîchir
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filtres + compteurs */}
          <div className="flex flex-wrap gap-2 mb-4">
            {STATUS_FILTERS.map((f) => {
              const count = f.value === 'all'
                ? (counts.pending_validation + counts.validated + counts.refused + (counts.awaiting_signature || 0))
                : counts[f.value] || 0;
              const active = filter === f.value;
              return (
                <button
                  key={f.value}
                  onClick={() => setFilter(f.value)}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                    active
                      ? 'bg-purple-600 text-white border-purple-700 shadow-sm'
                      : 'bg-white text-slate-700 border-slate-200 hover:border-purple-300 hover:bg-purple-50'
                  }`}
                  data-testid={`propositions-signees-filter-${f.value}`}
                >
                  {f.label}
                  <span
                    className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded text-[10px] font-bold ${
                      active ? 'bg-white/20 text-white' : f.color
                    }`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Liste */}
          {loading ? (
            <div className="text-center py-10 text-slate-500">
              <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
              Chargement…
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 px-6 bg-slate-50 rounded-lg border border-dashed border-slate-200">
              <ShieldCheck className="h-10 w-10 mx-auto text-slate-300 mb-2" />
              <p className="text-sm font-semibold text-slate-700">
                Aucune proposition {filter === 'pending_validation' ? 'à valider' : filter === 'validated' ? 'validée' : filter === 'refused' ? 'refusée' : ''} pour le moment.
              </p>
              <p className="text-xs text-slate-500 mt-1">
                {filter === 'pending_validation'
                  ? 'Les propositions signées par les destinataires apparaîtront ici.'
                  : 'Changez le filtre ci-dessus pour voir d\'autres statuts.'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {items.map((it) => {
                const isAwaiting = filter === 'awaiting_signature' || (
                  !it.signed_at_iso && (it.send_count || 0) > 0
                );
                const isPending = !isAwaiting && ((it.validation_status === 'pending_validation') || !it.validation_status);
                const isValidated = it.validation_status === 'validated';
                const isRefused = it.validation_status === 'refused';
                const isRelancing = relancingId === it.devis_id;
                return (
                  <div
                    key={it.devis_id}
                    className={`relative overflow-hidden rounded-xl border-l-4 bg-white shadow-sm hover:shadow-md transition-all ${
                      isAwaiting ? 'border-l-blue-500' :
                      isPending ? 'border-l-amber-500' :
                      isValidated ? 'border-l-emerald-500' :
                      'border-l-red-500'
                    }`}
                    data-testid={`proposition-signee-item-${it.devis_id}`}
                  >
                    {/* Halo decoratif top-right */}
                    <div className={`absolute -right-8 -top-8 w-32 h-32 rounded-full blur-3xl opacity-10 pointer-events-none ${
                      isAwaiting ? 'bg-blue-400' :
                      isPending ? 'bg-amber-400' :
                      isValidated ? 'bg-emerald-400' :
                      'bg-red-400'
                    }`} />
                    <div className="relative p-4 lg:p-5">
                      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                        {/* Bloc gauche : infos */}
                        <div className="flex-1 min-w-0">
                          {/* Badges header */}
                          <div className="flex items-center gap-2 flex-wrap mb-3">
                            <span
                              className="inline-flex items-center gap-1.5 font-mono text-xs bg-gradient-to-r from-slate-100 to-slate-50 border border-slate-300 px-2.5 py-1 rounded-md font-bold text-slate-800 shadow-sm"
                              data-testid={`proposition-devis-id-${it.devis_id}`}
                            >
                              <FileText className="h-3 w-3 text-slate-500" />
                              {it.devis_id}
                            </span>
                            {it.version > 1 && (
                              <Badge variant="outline" className="text-[10px] border-slate-400 text-slate-700 bg-slate-50">v{it.version}</Badge>
                            )}
                            {isAwaiting && (
                              <Badge className="bg-blue-600 text-white border-0 text-[10px] font-semibold shadow-sm">
                                <Send className="h-2.5 w-2.5 mr-1" />
                                En attente signature
                              </Badge>
                            )}
                            {isPending && (
                              <Badge className="bg-amber-600 text-white border-0 text-[10px] font-semibold shadow-sm">
                                <Clock className="h-2.5 w-2.5 mr-1" />
                                À valider
                              </Badge>
                            )}
                            {isValidated && (
                              <Badge className="bg-emerald-600 text-white border-0 text-[10px] font-semibold shadow-sm">
                                <Award className="h-2.5 w-2.5 mr-1" />
                                Validée
                              </Badge>
                            )}
                            {isRefused && (
                              <Badge className="bg-red-600 text-white border-0 text-[10px] font-semibold shadow-sm">
                                <XCircle className="h-2.5 w-2.5 mr-1" />
                                Refusée
                              </Badge>
                            )}
                            <Badge className="bg-purple-700 text-white border-0 text-[10px] font-semibold shadow-sm">
                              {it.type_souscription === 'nominative' ? 'RCDS Nominative' : 'RCDS Annuelle'}
                            </Badge>
                            {(it.relance_count || 0) > 0 && (
                              <Badge className="bg-orange-600 text-white border-0 text-[10px] font-semibold shadow-sm">
                                <BellRing className="h-2.5 w-2.5 mr-1" />
                                {it.relance_count} relance{it.relance_count > 1 ? 's' : ''}
                              </Badge>
                            )}
                          </div>

                          {/* Donnees principales en grille */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className="bg-slate-50 rounded-lg px-3 py-2 border border-slate-200">
                              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1">
                                <User className="h-3 w-3" /> Membre
                              </div>
                              <div className="text-sm font-bold text-slate-800 mt-0.5 truncate" title={it.membre_name}>{it.membre_name || '—'}</div>
                            </div>
                            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg px-3 py-2 border border-indigo-200">
                              <div className="text-[10px] uppercase tracking-wider text-indigo-600 font-semibold flex items-center gap-1">
                                <Euro className="h-3 w-3" /> Montant TTC
                              </div>
                              <div className="text-base font-bold text-indigo-700 mt-0.5">{fmtEur(it.montant_ttc)}</div>
                            </div>
                            {isAwaiting ? (
                              <>
                                <div className="bg-blue-50/60 rounded-lg px-3 py-2 border border-blue-200">
                                  <div className="text-[10px] uppercase tracking-wider text-blue-700 font-semibold">Destinataire</div>
                                  <div className="text-sm font-semibold text-slate-800 mt-0.5 truncate" title={it.last_sent_email}>{it.last_sent_email || '—'}</div>
                                </div>
                                <div className="bg-blue-50/60 rounded-lg px-3 py-2 border border-blue-200">
                                  <div className="text-[10px] uppercase tracking-wider text-blue-700 font-semibold">Envois</div>
                                  <div className="text-sm font-mono font-semibold text-slate-800 mt-0.5">
                                    {it.send_count || 0} initial(aux) · {it.relance_count || 0} relance(s)
                                  </div>
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="bg-slate-50 rounded-lg px-3 py-2 border border-slate-200 sm:col-span-1">
                                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1">
                                    <FileSignature className="h-3 w-3" /> Signé par
                                  </div>
                                  <div className="text-sm font-bold text-slate-800 mt-0.5 truncate">{it.signer_full_name || '—'}</div>
                                  <div className="text-[10px] text-slate-500 truncate">{it.signer_email}</div>
                                </div>
                                <div className="bg-slate-50 rounded-lg px-3 py-2 border border-slate-200">
                                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1">
                                    <Calendar className="h-3 w-3" /> Date signature
                                  </div>
                                  <div className="text-sm font-mono font-semibold text-slate-800 mt-0.5">{fmtDate(it.signed_at_iso)}</div>
                                </div>
                              </>
                            )}
                            {isRefused && it.validation_raison && (
                              <div className="sm:col-span-2 p-3 rounded-lg bg-gradient-to-br from-red-50 to-rose-50 border-l-4 border-red-500 text-red-900">
                                <div className="text-[10px] uppercase tracking-wider text-red-700 font-bold mb-0.5">Motif du refus</div>
                                <div className="text-sm">{it.validation_raison}</div>
                              </div>
                            )}
                            {isValidated && it.validation_at_iso && (
                              <div className="sm:col-span-2 px-3 py-2 rounded-lg bg-gradient-to-br from-emerald-50 to-teal-50 border-l-4 border-emerald-500 text-[11px] text-emerald-800">
                                <CheckCircle2 className="inline h-3 w-3 mr-1" />
                                Validée le <strong>{fmtDate(it.validation_at_iso)}</strong>
                                {it.validation_by_email ? <> par <strong>{it.validation_by_email}</strong></> : ''}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Bloc droite : actions */}
                        <div className="flex flex-row lg:flex-col gap-2 lg:min-w-[220px] shrink-0">
                          {isAwaiting && (
                            <Button
                              size="sm"
                              onClick={() => handleRelancer(it)}
                              disabled={isRelancing}
                              className="w-full text-xs bg-orange-600 hover:bg-orange-700 text-white shadow-sm"
                              data-testid={`proposition-relancer-${it.devis_id}`}
                            >
                              {isRelancing
                                ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                                : <BellRing className="h-3.5 w-3.5 mr-1" />
                              }
                              Renvoyer maintenant
                            </Button>
                          )}
                          {!isAwaiting && it.signed_pdf_available && (
                            <div className="grid grid-cols-2 gap-1.5 w-full">
                              <Button
                                size="sm"
                                onClick={() => handleViewPdf(it)}
                                disabled={pdfLoadingId === it.devis_id}
                                className="text-xs bg-slate-800 hover:bg-slate-900 text-white shadow-sm"
                                data-testid={`proposition-signee-view-${it.devis_id}`}
                                title="Visualiser le PDF signé"
                              >
                                {pdfLoadingId === it.devis_id
                                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  : <><Eye className="h-3.5 w-3.5 mr-1" />Voir</>}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDownloadPdf(it)}
                                disabled={pdfLoadingId === it.devis_id}
                                className="text-xs border-slate-300 hover:bg-slate-50"
                                data-testid={`proposition-signee-pdf-${it.devis_id}`}
                                title="Telecharger le PDF signé"
                              >
                                <Download className="h-3.5 w-3.5 mr-1" />
                                PDF
                              </Button>
                            </div>
                          )}
                          {isPending && (
                            <>
                              <Button
                                size="sm"
                                onClick={() => openValider(it)}
                                className="w-full text-xs bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm"
                                data-testid={`proposition-signee-valider-${it.devis_id}`}
                              >
                                <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                                Valider / Créer
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openRefuser(it)}
                                className="w-full text-xs border-red-300 text-red-700 hover:bg-red-50"
                                data-testid={`proposition-signee-refuser-${it.devis_id}`}
                              >
                                <XCircle className="h-3.5 w-3.5 mr-1" />
                                Refuser
                              </Button>
                            </>
                          )}
                          {/* V37.80 — Journal des emails envoyés (traçabilité) */}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleShowEmailLog(it)}
                            disabled={emailLogLoadingId === it.devis_id}
                            className="w-full text-xs border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                            data-testid={`proposition-email-log-${it.devis_id}`}
                            title="Voir tous les emails envoyés pour cette proposition (qui, quoi, à qui, quand)"
                          >
                            {emailLogLoadingId === it.devis_id
                              ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                              : <History className="h-3.5 w-3.5 mr-1" />}
                            Journal emails
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <PropositionsSigneesModals
        pdfPreview={pdfPreview} closePdfPreview={closePdfPreview}
        resendOpen={resendOpen} setResendOpen={setResendOpen}
        resendForm={resendForm} setResendForm={setResendForm}
        handleResendPdfByEmail={handleResendPdfByEmail} resendSending={resendSending}
        refuserOpen={refuserOpen} setRefuserOpen={setRefuserOpen}
        refuserTarget={refuserTarget} refuserRaison={refuserRaison}
        setRefuserRaison={setRefuserRaison} refuserSubmitting={refuserSubmitting} submitRefuser={submitRefuser}
        validerOpen={validerOpen} setValiderOpen={setValiderOpen}
        validerTarget={validerTarget} validerNote={validerNote}
        setValiderNote={setValiderNote} validerSubmitting={validerSubmitting} submitValider={submitValider}
        emailLog={emailLog} setEmailLog={setEmailLog}
      />

    </div>
  );
}
